import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { Plus, Package, TrendingDown, TrendingUp, DollarSign, RotateCcw, History, Edit, Calendar } from 'lucide-react';
import { useBranch } from '../../contexts/BranchContext';
import { useAuth } from '../../contexts/AuthContext';

const Inventory = () => {
  const { selectedBranch } = useBranch();
  const { currencySymbol } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('stock');
  const [historyPage, setHistoryPage] = useState(1);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  const [returnData, setReturnData] = useState({
    quantityToReturn: '',
    notes: ''
  });

  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseItems, setPurchaseItems] = useState([{
    productId: '',
    quantity: '',
    rate: '',
    gstAmount: ''
  }]);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [supplierFormData, setSupplierFormData] = useState({
    name: '',
    contactPerson: '',
    phone: '',
    email: '',
    address: '',
    gstNumber: ''
  });

  const [purchaseData, setPurchaseData] = useState({
    supplierId: '',
    invoiceNumber: '',
    paymentMode: 'CASH',
    notes: ''
  });

  // Product profit date filter state
  const [profitStartDate, setProfitStartDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
  });
  const [profitEndDate, setProfitEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    id: '',
    productId: '',
    supplierId: '',
    invoiceNumber: '',
    quantity: '',
    rate: '',
    gstAmount: '',
    paymentMode: 'CASH',
    notes: ''
  });

  // Fetch inventory summary
  const { data: summaryData } = useQuery(
    ['inventory-summary', selectedBranch?.id],
    async () => {
      const response = await api.get('/api/inventory/summary', {
        params: { branchId: selectedBranch?.id }
      });
      return response.data.data;
    },
    {
      enabled: !!selectedBranch,
    }
  );

  // Fetch stock data
  const { data: stockData, isLoading } = useQuery(
    ['inventory-stock', selectedBranch?.id],
    async () => {
      const response = await api.get('/api/inventory/stock', {
        params: { branchId: selectedBranch?.id }
      });
      return response.data.data;
    },
    {
      enabled: !!selectedBranch,
    }
  );

  // Fetch products for purchase
  const { data: productsData } = useQuery(
    ['products', selectedBranch?.id],
    async () => {
      const response = await api.get('/api/products', {
        params: { branchId: selectedBranch?.id }
      });
      return response.data.data.products;
    },
    {
      enabled: !!selectedBranch,
    }
  );

  // Fetch suppliers
  const { data: suppliersData } = useQuery('suppliers', async () => {
    const response = await api.get('/api/suppliers');
    return response.data.data;
  });

  // Fetch product profit data
  const { data: profitData, isLoading: isProfitLoading } = useQuery(
    ['inventory-profit', selectedBranch?.id, profitStartDate, profitEndDate],
    async () => {
      const response = await api.get('/api/inventory/product-profit', {
        params: {
          branchId: selectedBranch?.id,
          startDate: profitStartDate,
          endDate: profitEndDate
        }
      });
      return response.data.data;
    },
    {
      enabled: !!selectedBranch && activeTab === 'profit',
    }
  );

  // Fetch transactions (purchases)
  const { data: transactionsData, isLoading: isTxLoading } = useQuery(
    ['inventory-transactions', selectedBranch?.id, historyPage],
    async () => {
      const response = await api.get('/api/inventory/transactions', {
        params: { 
          branchId: selectedBranch?.id,
          type: 'PURCHASE',
          page: historyPage,
          limit: 10
        }
      });
      return response.data.data;
    },
    {
      enabled: !!selectedBranch && activeTab === 'history',
    }
  );

  const createReturnMutation = useMutation(
    async (data) => {
      const response = await api.post('/api/inventory/purchase-return', data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Purchase return recorded successfully!');
        queryClient.invalidateQueries('inventory-summary');
        queryClient.invalidateQueries('inventory-stock');
        queryClient.invalidateQueries('inventory-transactions');
        queryClient.invalidateQueries('products');
        setShowReturnModal(false);
        setSelectedTx(null);
        setReturnData({ quantityToReturn: '', notes: '' });
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to record return');
      }
    }
  );

  const createSupplierMutation = useMutation(
    async (data) => {
      const response = await api.post('/api/suppliers', data);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success('Supplier created successfully!');
        queryClient.invalidateQueries('suppliers');
        setPurchaseData(prev => ({ ...prev, supplierId: data.data.id }));
        setShowSupplierModal(false);
        setSupplierFormData({
          name: '',
          contactPerson: '',
          phone: '',
          email: '',
          address: '',
          gstNumber: ''
        });
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create supplier');
      }
    }
  );

  const createPurchaseMutation = useMutation(
    async (data) => {
      const response = await api.post('/api/inventory/purchase', data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Purchase recorded successfully!');
        queryClient.invalidateQueries('inventory-summary');
        queryClient.invalidateQueries('inventory-stock');
        queryClient.invalidateQueries('products');
        setShowPurchaseModal(false);
        resetPurchaseForm();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to record purchase');
      }
    }
  );

  const updatePurchaseMutation = useMutation(
    async ({ id, data }) => {
      const response = await api.put(`/api/inventory/purchase/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Purchase updated successfully!');
        queryClient.invalidateQueries('inventory-summary');
        queryClient.invalidateQueries('inventory-stock');
        queryClient.invalidateQueries('inventory-transactions');
        queryClient.invalidateQueries('products');
        setShowEditModal(false);
        setEditFormData({
          id: '',
          productId: '',
          supplierId: '',
          invoiceNumber: '',
          quantity: '',
          rate: '',
          gstAmount: '',
          paymentMode: 'CASH',
          notes: ''
        });
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update purchase');
      }
    }
  );

  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (!editFormData.productId || !editFormData.quantity || !editFormData.rate) {
      toast.error('Product, quantity, and rate are required.');
      return;
    }
    updatePurchaseMutation.mutate({
      id: editFormData.id,
      data: {
        productId: editFormData.productId,
        supplierId: editFormData.supplierId || null,
        invoiceNumber: editFormData.invoiceNumber,
        quantity: editFormData.quantity,
        rate: editFormData.rate,
        gstAmount: editFormData.gstAmount,
        paymentMode: editFormData.paymentMode,
        notes: editFormData.notes
      }
    });
  };

  const handlePurchaseSubmit = (e) => {
    e.preventDefault();
    
    const validItems = purchaseItems.filter(item => 
      item.productId && item.quantity && item.rate
    );

    if (validItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    createPurchaseMutation.mutate({
      ...purchaseData,
      branchId: selectedBranch?.id,
      items: validItems.map(item => ({
        ...item,
        quantity: parseInt(item.quantity),
        rate: parseFloat(item.rate),
        gstAmount: parseFloat(item.gstAmount) || 0
      }))
    });
  };

  const addPurchaseItem = () => {
    setPurchaseItems([...purchaseItems, {
      productId: '',
      quantity: '',
      rate: '',
      gstAmount: ''
    }]);
  };

  const removePurchaseItem = (index) => {
    setPurchaseItems(purchaseItems.filter((_, i) => i !== index));
  };

  const updatePurchaseItem = (index, field, value) => {
    const newItems = [...purchaseItems];
    newItems[index][field] = value;
    setPurchaseItems(newItems);
  };

  const resetPurchaseForm = () => {
    setPurchaseItems([{
      productId: '',
      quantity: '',
      rate: '',
      gstAmount: ''
    }]);
    setPurchaseData({
      supplierId: '',
      invoiceNumber: '',
      paymentMode: 'CASH',
      notes: ''
    });
  };

  const summary = summaryData || {};
  const stockItems = stockData || [];
  const products = productsData || [];
  const suppliers = suppliersData || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventory Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage stock levels and purchase orders
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={() => setShowPurchaseModal(true)}
            className="btn-primary inline-flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Purchase
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium opacity-90">Stock Quantity</p>
              <p className="text-3xl font-bold mt-2">{summary.totalStock || 0}</p>
            </div>
            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
              <Package className="w-8 h-8" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium opacity-90">Low Stock Quantity</p>
              <p className="text-3xl font-bold mt-2">{summary.lowStockCount || 0}</p>
            </div>
            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
              <TrendingDown className="w-8 h-8" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium opacity-90"> Stock Value</p>
              <p className="text-3xl font-bold mt-2">{currencySymbol}{(summary.stockValue || 0).toLocaleString()}</p>
            </div>
            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
              <DollarSign className="w-8 h-8" />
            </div>
          </div>
        </div>
      </div>

      {/* Stock Levels & Purchase History Tabs */}
      <div className="card p-0">
        <div className="flex border-b border-gray-200 bg-gray-50 rounded-t-lg px-6 pt-4 space-x-6">
          <button
            onClick={() => setActiveTab('stock')}
            className={`pb-4 text-sm font-semibold flex items-center border-b-2 transition-all ${
              activeTab === 'stock'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Package className="w-4 h-4 mr-2" />
            Stock Levels
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`pb-4 text-sm font-semibold flex items-center border-b-2 transition-all ${
              activeTab === 'history'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <History className="w-4 h-4 mr-2" />
            Purchase History
          </button>
          <button
            onClick={() => setActiveTab('profit')}
            className={`pb-4 text-sm font-semibold flex items-center border-b-2 transition-all ${
              activeTab === 'profit'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Profit History
          </button>
        </div>

        {activeTab === 'stock' ? (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Branch</th>
                  <th>Category</th>
                  <th>Brand</th>
                  <th>Model</th>
                  <th>Item Name</th>
                  <th> Stock</th>
                  <th>Stock Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {stockItems.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td>{selectedBranch?.name}</td>
                    <td>{item.category?.name}</td>
                    <td>{item.brand}</td>
                    <td>{item.model}</td>
                    <td className="font-medium">{item.name}</td>
                    <td>
                      <span className={`font-semibold ${
                        item.currentStock <= item.minStockLimit
                          ? 'text-red-600'
                          : 'text-gray-900'
                      }`}>
                        {item.currentStock}
                      </span>
                    </td>
                    <td className="font-medium">
                      {currencySymbol}{(item.currentStock * item.saleRate).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {stockItems.length === 0 && (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No stock data available</p>
              </div>
            )}
          </div>
        ) : activeTab === 'profit' ? (
          <div>
            {/* Date Filter */}
            <div className="px-6 pt-4 pb-2 flex items-center gap-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <label className="text-xs font-semibold text-gray-600">From:</label>
                <input
                  type="date"
                  className="input-field text-sm py-1.5"
                  value={profitStartDate}
                  onChange={(e) => setProfitStartDate(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <label className="text-xs font-semibold text-gray-600">To:</label>
                <input
                  type="date"
                  className="input-field text-sm py-1.5"
                  value={profitEndDate}
                  onChange={(e) => setProfitEndDate(e.target.value)}
                />
              </div>
            </div>
            {isProfitLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner w-8 h-8"></div>
              </div>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Brand</th>
                      <th>Model</th>
                      <th>Purchase Rate</th>
                      <th>Sale Rate</th>
                      <th>Qty Sold</th>
                      <th>Total Purchase Cost</th>
                      <th>Total Sale</th>
                      <th>Profit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(profitData?.products || []).map((item, idx) => (
                      <tr key={item.productId || idx} className="hover:bg-gray-50">
                        <td className="font-medium text-gray-900">{item.productName}</td>
                        <td>{item.brand}</td>
                        <td>{item.model}</td>
                        <td>{currencySymbol}{item.purchaseRate.toFixed(2)}</td>
                        <td>{currencySymbol}{item.saleRate.toFixed(2)}</td>
                        <td className="text-center font-semibold">{item.totalQuantity}</td>
                        <td className="font-semibold text-orange-600">{currencySymbol}{item.totalPurchaseCost.toFixed(2)}</td>
                        <td className="font-semibold text-green-600">{currencySymbol}{item.totalSaleAmount.toFixed(2)}</td>
                        <td className={`font-bold ${item.totalProfit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {currencySymbol}{item.totalProfit.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(profitData?.products || []).length === 0 && (
                  <div className="text-center py-12">
                    <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No sales data available for this period</p>
                  </div>
                )}
                {profitData?.products?.length > 0 && (
                  <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Total Purchase Cost</p>
                        <p className="text-lg font-bold text-orange-700">{currencySymbol}{(profitData.totals?.totalPurchaseCost || 0).toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Total Sale</p>
                        <p className="text-lg font-bold text-green-700">{currencySymbol}{(profitData.totals?.totalSale || 0).toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Total Qty Sold</p>
                        <p className="text-lg font-bold text-gray-900">{profitData.totals?.totalQuantity || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Total Profit</p>
                        <p className={`text-lg font-bold ${(profitData.totals?.totalProfit || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {currencySymbol}{(profitData.totals?.totalProfit || 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div>
            {isTxLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner w-8 h-8"></div>
              </div>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Invoice Number</th>
                      <th>Supplier</th>
                      <th>Product</th>
                      <th>Brand</th>
                      <th>Model</th>
                      <th>Quantity</th>
                      <th>Rate</th>
                      <th>Total Amount</th>
                      <th>Payment Mode</th>
                      <th>Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(transactionsData?.transactions || []).map((tx) => {
                      const isReturn = tx.type === 'PURCHASE_RETURN';
                      return (
                        <tr key={tx.id} className={`hover:bg-gray-50 ${isReturn ? 'bg-red-50 hover:bg-red-100' : ''}`}>
                          <td>{new Date(tx.transactionDate).toLocaleDateString()}</td>
                          <td className="font-medium">{tx.invoiceNumber || 'N/A'}</td>
                          <td>{tx.supplier?.name || 'N/A'}</td>
                          <td>{tx.product?.name}</td>
                          <td>{tx.product?.brand || '-'}</td>
                          <td>{tx.product?.model || '-'}</td>
                          <td className={`font-semibold ${isReturn ? 'text-red-600' : ''}`}>{tx.quantity}</td>
                          <td>{currencySymbol}{tx.rate.toFixed(2)}</td>
                          <td className={`font-semibold ${isReturn ? 'text-red-600' : ''}`}>{currencySymbol}{tx.totalAmount.toFixed(2)}</td>
                          <td>
                            <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              tx.paymentMode === 'CREDIT' 
                                ? 'bg-orange-100 text-orange-800' 
                                : isReturn 
                                  ? 'bg-red-100 text-red-800' 
                                  : 'bg-green-100 text-green-800'
                            }`}>
                              {tx.paymentMode || 'N/A'}
                            </span>
                          </td>
                          <td className="max-w-xs truncate" title={tx.notes}>{tx.notes || '-'}</td>
                          <td>
                            <div className="flex items-center space-x-3">
                              {!isReturn && tx.quantity > 0 && (
                                <>
                                  <button
                                    onClick={() => {
                                      setEditFormData({
                                        id: tx.id,
                                        productId: tx.productId,
                                        supplierId: tx.supplierId || '',
                                        invoiceNumber: tx.invoiceNumber || '',
                                        quantity: tx.quantity.toString(),
                                        rate: tx.rate.toString(),
                                        gstAmount: (tx.gstAmount || 0).toString(),
                                        paymentMode: tx.paymentMode || 'CASH',
                                        notes: tx.notes || ''
                                      });
                                      setShowEditModal(true);
                                    }}
                                    className="text-blue-600 hover:text-blue-900 font-semibold text-sm flex items-center"
                                  >
                                    <Edit className="w-4 h-4 mr-1" />
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => {
                                      setSelectedTx(tx);
                                      setReturnData({
                                        quantityToReturn: tx.quantity.toString(),
                                        notes: ''
                                      });
                                      setShowReturnModal(true);
                                    }}
                                    className="text-red-650 hover:text-red-900 font-semibold text-sm flex items-center"
                                  >
                                    <RotateCcw className="w-4 h-4 mr-1" />
                                    Return
                                  </button>
                                </>
                              )}
                              {isReturn && (
                                <span className="text-red-600 font-semibold text-xs bg-red-105 px-2 py-0.5 rounded">
                                  Returned
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {(transactionsData?.transactions || []).length === 0 && (
                  <div className="text-center py-12">
                    <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No purchase history available</p>
                  </div>
                )}
                
                {transactionsData?.pagination?.totalPages > 1 && (
                  <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
                    <div className="text-sm text-gray-500">
                      Showing Page {historyPage} of {transactionsData.pagination.totalPages}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setHistoryPage(prev => Math.max(prev - 1, 1))}
                        disabled={!transactionsData.pagination.hasPrev}
                        className="btn-secondary text-sm disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setHistoryPage(prev => prev + 1)}
                        disabled={!transactionsData.pagination.hasNext}
                        className="btn-secondary text-sm disabled:opacity-50"
                        >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Purchase Modal */}
      {showPurchaseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold">Add Purchase</h2>
              <button
                onClick={() => {
                  setShowPurchaseModal(false);
                  resetPurchaseForm();
                }}
                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg"
              >
                ×
              </button>
            </div>

            <form onSubmit={handlePurchaseSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Supplier *</label>
                  <div className="flex items-center space-x-2">
                    <select
                      className="input-field flex-1"
                      value={purchaseData.supplierId}
                      onChange={(e) => setPurchaseData({ ...purchaseData, supplierId: e.target.value })}
                      required
                    >
                      <option value="">Select Supplier</option>
                      {suppliers.map(supplier => (
                        <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowSupplierModal(true)}
                      className="btn-secondary p-2.5"
                      title="Add New Supplier"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="form-label">Invoice Number</label>
                  <input
                    type="text"
                    className="input-field"
                    value={purchaseData.invoiceNumber}
                    onChange={(e) => setPurchaseData({ ...purchaseData, invoiceNumber: e.target.value })}
                  />
                </div>
                <div>
                  <label className="form-label">Payment Mode *</label>
                  <select
                    className="input-field"
                    value={purchaseData.paymentMode}
                    onChange={(e) => setPurchaseData({ ...purchaseData, paymentMode: e.target.value })}
                    required
                  >
                    <option value="CASH">Cash</option>
                    <option value="BANK">Bank</option>
                    <option value="UPI">UPI</option>
                    <option value="CARD">Credit/Debit Card</option>
                    <option value="CREDIT">Credit (Pay Later / Supplier Credit)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="form-label">Notes</label>
                <textarea
                  className="input-field"
                  rows="2"
                  value={purchaseData.notes}
                  onChange={(e) => setPurchaseData({ ...purchaseData, notes: e.target.value })}
                />
              </div>

              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Items</h3>
                  <button
                    type="button"
                    onClick={addPurchaseItem}
                    className="btn-secondary text-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Item
                  </button>
                </div>

                <div className="space-y-3">
                  {purchaseItems.map((item, index) => (
                    <div key={index} className="grid grid-cols-5 gap-3 items-end">
                      <div className="col-span-2">
                        <label className="form-label text-xs">Product</label>
                        <Select
                          options={products.map(p => ({
                            value: p.id,
                            label: `${p.name} - ${p.brand} ${p.model}`
                          }))}
                          value={item.productId ? { value: item.productId, label: products.find(p => p.id === item.productId)?.name + ' - ' + products.find(p => p.id === item.productId)?.brand + ' ' + products.find(p => p.id === item.productId)?.model } : null}
                          onChange={(selected) => updatePurchaseItem(index, 'productId', selected ? selected.value : '')}
                          placeholder="Search product..."
                          isSearchable
                          className="react-select-container text-sm"
                          classNamePrefix="react-select"
                          noOptionsMessage={() => 'No products found'}
                        />
                      </div>
                      <div>
                        <label className="form-label text-xs">Quantity</label>
                        <input
                          type="number"
                          className="input-field text-sm"
                          value={item.quantity}
                          onChange={(e) => updatePurchaseItem(index, 'quantity', e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <label className="form-label text-xs">Rate</label>
                        <input
                          type="number"
                          className="input-field text-sm"
                          value={item.rate}
                          onChange={(e) => updatePurchaseItem(index, 'rate', e.target.value)}
                          step="0.01"
                          required
                        />
                      </div>
                      <div className="flex items-end space-x-2">
                        <div className="flex-1">
                          <label className="form-label text-xs">GST</label>
                          <input
                            type="number"
                            className="input-field text-sm"
                            value={item.gstAmount}
                            onChange={(e) => updatePurchaseItem(index, 'gstAmount', e.target.value)}
                            step="0.01"
                          />
                        </div>
                        {purchaseItems.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removePurchaseItem(index)}
                            className="btn-danger text-sm p-2"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end space-x-4 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowPurchaseModal(false);
                    resetPurchaseForm();
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createPurchaseMutation.isLoading}
                  className="btn-primary"
                >
                  {createPurchaseMutation.isLoading ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    'Record Purchase'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Supplier Modal */}
      {showSupplierModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold">Add New Supplier</h2>
              <button
                onClick={() => setShowSupplierModal(false)}
                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg"
              >
                ×
              </button>
            </div>

            <form 
              onSubmit={(e) => {
                e.preventDefault();
                createSupplierMutation.mutate(supplierFormData);
              }} 
              className="p-6 space-y-4"
            >
              <div>
                <label className="form-label">Supplier Name *</label>
                <input
                  type="text"
                  className="input-field"
                  value={supplierFormData.name}
                  onChange={(e) => setSupplierFormData({ ...supplierFormData, name: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Contact Person</label>
                  <input
                    type="text"
                    className="input-field"
                    value={supplierFormData.contactPerson}
                    onChange={(e) => setSupplierFormData({ ...supplierFormData, contactPerson: e.target.value })}
                  />
                </div>
                <div>
                  <label className="form-label">Phone</label>
                  <input
                    type="tel"
                    className="input-field"
                    value={supplierFormData.phone}
                    onChange={(e) => setSupplierFormData({ ...supplierFormData, phone: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="input-field"
                  value={supplierFormData.email}
                  onChange={(e) => setSupplierFormData({ ...supplierFormData, email: e.target.value })}
                />
              </div>

              <div>
                <label className="form-label">Address</label>
                <textarea
                  className="input-field"
                  rows="2"
                  value={supplierFormData.address}
                  onChange={(e) => setSupplierFormData({ ...supplierFormData, address: e.target.value })}
                />
              </div>

              <div>
                <label className="form-label">GST Number</label>
                <input
                  type="text"
                  className="input-field"
                  value={supplierFormData.gstNumber}
                  onChange={(e) => setSupplierFormData({ ...supplierFormData, gstNumber: e.target.value })}
                />
              </div>

              <div className="flex items-center justify-end space-x-4 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowSupplierModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createSupplierMutation.isLoading}
                  className="btn-primary"
                >
                  {createSupplierMutation.isLoading ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    'Create Supplier'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Return Modal */}
      {showReturnModal && selectedTx && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="sticky top-0 bg-gradient-to-r from-red-650 to-red-750 text-white px-6 py-4 flex items-center justify-between rounded-t-lg">
              <h2 className="text-xl font-bold flex items-center">
                <RotateCcw className="w-5 h-5 mr-2" />
                Return Purchase Item
              </h2>
              <button
                onClick={() => {
                  setShowReturnModal(false);
                  setSelectedTx(null);
                }}
                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg"
              >
                ×
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                createReturnMutation.mutate({
                  originalTransactionId: selectedTx.id,
                  quantityToReturn: parseInt(returnData.quantityToReturn),
                  notes: returnData.notes
                });
              }}
              className="p-6 space-y-4"
            >
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Product</label>
                <div className="text-sm font-medium text-gray-900 bg-gray-55 p-2.5 rounded border">{selectedTx.product?.name}</div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Original Invoice</label>
                  <div className="text-sm font-medium text-gray-900 bg-gray-55 p-2.5 rounded border">{selectedTx.invoiceNumber || 'N/A'}</div>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Original Qty</label>
                  <div className="text-sm font-medium text-gray-900 bg-gray-55 p-2.5 rounded border">{selectedTx.quantity}</div>
                </div>
              </div>

              <div>
                <label className="form-label font-semibold">Quantity to Return *</label>
                <input
                  type="number"
                  className="input-field"
                  min="1"
                  max={selectedTx.quantity}
                  value={returnData.quantityToReturn}
                  onChange={(e) => setReturnData({ ...returnData, quantityToReturn: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="form-label font-semibold">Reason for Return</label>
                <textarea
                  className="input-field"
                  rows="3"
                  placeholder="E.g., Damaged items, wrong parts delivered..."
                  value={returnData.notes}
                  onChange={(e) => setReturnData({ ...returnData, notes: e.target.value })}
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowReturnModal(false);
                    setSelectedTx(null);
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createReturnMutation.isLoading}
                  className="btn-danger inline-flex items-center"
                >
                  {createReturnMutation.isLoading ? 'Recording...' : 'Confirm Return'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Edit Purchase Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex items-center justify-between rounded-t-lg">
              <h2 className="text-xl font-bold flex items-center">
                <Edit className="w-5 h-5 mr-2" />
                Edit Purchase Transaction
              </h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                }}
                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Supplier *</label>
                  <select
                    className="input-field"
                    value={editFormData.supplierId}
                    onChange={(e) => setEditFormData({ ...editFormData, supplierId: e.target.value })}
                  >
                    <option value="">Select Supplier</option>
                    {suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Invoice Number</label>
                  <input
                    type="text"
                    className="input-field"
                    value={editFormData.invoiceNumber}
                    onChange={(e) => setEditFormData({ ...editFormData, invoiceNumber: e.target.value })}
                  />
                </div>
                <div>
                  <label className="form-label">Payment Mode *</label>
                  <select
                    className="input-field"
                    value={editFormData.paymentMode}
                    onChange={(e) => setEditFormData({ ...editFormData, paymentMode: e.target.value })}
                    required
                  >
                    <option value="CASH">Cash</option>
                    <option value="BANK">Bank</option>
                    <option value="UPI">UPI</option>
                    <option value="CARD">Credit/Debit Card</option>
                    <option value="CREDIT">Credit (Pay Later / Supplier Credit)</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Product *</label>
                  <Select
                    options={products.map(p => ({
                      value: p.id,
                      label: `${p.name} - ${p.brand || ''} ${p.model || ''}`
                    }))}
                    value={(() => {
                      const prod = products.find(p => p.id === editFormData.productId);
                      return prod ? { value: prod.id, label: `${prod.name} - ${prod.brand || ''} ${prod.model || ''}` } : null;
                    })()}
                    onChange={(selected) => setEditFormData({ ...editFormData, productId: selected ? selected.value : '' })}
                    placeholder="Search product..."
                    isSearchable
                    className="react-select-container text-sm"
                    classNamePrefix="react-select"
                    noOptionsMessage={() => 'No products found'}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Quantity *</label>
                  <input
                    type="number"
                    className="input-field"
                    value={editFormData.quantity}
                    onChange={(e) => setEditFormData({ ...editFormData, quantity: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Rate *</label>
                  <input
                    type="number"
                    className="input-field"
                    value={editFormData.rate}
                    onChange={(e) => setEditFormData({ ...editFormData, rate: e.target.value })}
                    step="0.01"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">GST Amount</label>
                  <input
                    type="number"
                    className="input-field"
                    value={editFormData.gstAmount}
                    onChange={(e) => setEditFormData({ ...editFormData, gstAmount: e.target.value })}
                    step="0.01"
                  />
                </div>
              </div>

              <div>
                <label className="form-label">Notes</label>
                <textarea
                  className="input-field"
                  rows="2"
                  value={editFormData.notes}
                  onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                />
              </div>

              <div className="flex items-center justify-end space-x-4 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updatePurchaseMutation.isLoading}
                  className="btn-primary"
                >
                  {updatePurchaseMutation.isLoading ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Updating...
                    </>
                  ) : (
                    'Update Purchase'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;

